import React, { useState, useRef, useEffect } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Card, Stack, Grid, ImageList, ImageListItem,Icon, Dialog, IconButton, DialogContent, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Box, CircularProgress, CardContent } from "@mui/material";
import { initialValues, categoryOptions } from "./Product.constants";
import { Form, FormikProvider, useFormik } from "formik";
import { is } from "src/util/is";
import {
  issueImgUpload,
} from "../ServiceRequest/serviceRequest.hook";
import { useMutation } from "@tanstack/react-query";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import ImgPreviewModal from "../ImagePreviewModal/ImgPreviewModal";
import { productFormvalidationSchema } from "src/util/validationSchemas";
import { GridCloseIcon } from "@mui/x-data-grid";
import { Loader } from "../Loader";
import {
  useGetDetail,
  useMutate,
  useList,
} from "src/hooks/useFetch.hook";
import {
  productCatalogUrl,
  getProductCatalogUrlId
} from "src/react-query/endPoints";
import { BackButton } from "src/common/components/BackButton";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums, { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { isValidImageType, MAX_IMAGE_SIZE_MB } from "src/utils/fileTypes";

const ProductForm = () => {
      const [isUploadingIcon, setIsUploadingIcon] = useState(false);
      const iconUploadInputRef = useRef();
      const dispatch = useDispatch();
      const { actions: { setAlert } } = alertSlice;
  const isReadOnly = useAccess(FEATURE.PRODUCT_LIST, "R");
      const imgUpload = useMutation(issueImgUpload);
      const { mutate, isLoading } = useMutate("productCatalog");
      const [imageUploadPending, setImageUploadPending] = useState(false);
      const isProductId = useMatch("/product-listing/:productId");
      const newProductList = useMatch("/product-listing/new");
      const { id: productId } = useParams();
      const navigate = useNavigate();
      const [photoModal, setPhotoModal] = useState({
        open: false,
        src: "",
        type: "",
      });
      const { data: productData, isFetching } = useGetDetail(
        "getProductDetails",
         { id: productId },
         {
            enabled: !!productId, // Only fetch if id exists
        }
      );
      const { data: category, isFetching: isFetchingCategory, refetch } = useList('productCategory')

      const onSubmit = (values, { setSubmitting }) => {
          // Backend API call to save the product catalog
          const mutateObject = {
              url: productCatalogUrl,
              method: "POST",
          };
      
          if (isProductId && !is.nullOrUndefined(productId)) {
              mutateObject.method = "PUT";
              mutateObject.url = getProductCatalogUrlId(productId);
          }
          mutate(
              {
              url: mutateObject.url,
              method: mutateObject.method,
              body: values,
              },
              { onSuccess }
          );
          setSubmitting(false);
      };
      
      const onSuccess = () => navigate("/product-listing");
      
      const formik = useFormik({
          initialValues: initialValues,
          validationSchema: productFormvalidationSchema,
          onSubmit,
      });
      
      const {
          errors,
          touched,
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          getFieldProps,
          resetForm,
          setFieldValue,
          setErrors,
        } = formik;
      

          const handleImageUpload = (e, imgType) => {
            if (isReadOnly) {
              return;
            }
            const file = e.target.files[0];
            if (!file) {
              return;
            }
          // Validate file type
              if (!isValidImageType(file.type)) {
                dispatch(setAlert({
                        open: true,
                        message: `Only JPEG, JPG, PNG, WEBP or SVG images are allowed.`,
                        severity: enums.SEVERITY.ERROR,
                      }));
                return;
              }
        
              // Validate file size
              if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                dispatch(setAlert({
                  open: true,
                  message: `File size exceeds ${MAX_IMAGE_SIZE_MB} MB.`,
                  severity: enums.SEVERITY.ERROR,
                }));
                return;
              }

            const updatedImagePreview = {
              ...imagePreview,
              open: true,
              value: file,
              imgType,
            };
            setImagePreview(updatedImagePreview);
          };
        
            const uploadImageHandler = async (file, imgType) => {
                if (isReadOnly) {
                  return;
                }
                if (imgType === "icon") {
                setIsUploadingIcon(true);
                } 
                imgUpload.mutate(
                {
                    image: file,
                    folder: "icon",
                },
                {
                    onSuccess: (response) => {
                    if (imgType === "icon") {
                        setIsUploadingIcon(false);
                    } 
                    setImageUploadPending(false);
                    let tnCurl = response.data?.data?.url;
                    if (imgType === "icon") {
                        formik.setFieldValue("icon", tnCurl);
                    }
                    },
                }
                );
                closePreviewModal();
            };
        
            const cancelImageUpload = (imgType) => {
                if (isReadOnly) {
                  return;
                }
                formik.setFieldValue(`icon`, "");
                closePreviewModal();
            };
        
            const closePreviewModal = () => {
                setImagePreview({ ...imagePreview, open: false });
            };

            const imagePreviewButtons = isReadOnly
              ? []
              : [
                  {
                    btnLabel: "Upload",
                    action: uploadImageHandler,
                    params: true,
                  },
                  {
                    btnLabel: "Cancel",
                    action: cancelImageUpload,
                    params: true,
                  },
                ];
        
            const [imagePreview, setImagePreview] = useState({
              open: false,
              value: "",
              imgType: "",
            });
        
            useEffect(() => {
                if (newProductList !== "product list") {
                  if (productData && productId) {
                    resetForm({
                      values: {
                        icon: productData[0]?.icon || "",
                        title: productData[0]?.title || "",
                        description: productData[0]?.description || "",
                        url: productData[0]?.url || "",
                        access_type: productData[0]?.access_type ?? "",
                        category: productData[0]?.category || "",
                      },
                    });
                  } else {
                    resetForm({ values: initialValues });
                  }
                } else {
                  // Reset form when switching to another tab if needed
                  resetForm({ values: initialValues });
                }
              }, [productData, productId, resetForm]);
              
            
            if (isFetching) {
                return <Loader />;
            }

    return(
        <>  
          <BackButton
              text={
              "Product Listing"
              }
              path="/product-listing"
          />
          <Card sx={{ p: 2 }}>
          <FormikProvider value={formik} >
              <Form
                  autoComplete="off"
                  id="product-form"
                  noValidate
                  onSubmit={handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={6}>
                      <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          direction="row"
                          justifyContent="flex-start"
                          flexWrap="wrap"
                          spacing={2}
                      >
                      {isUploadingIcon ? (
                          <Box
                          sx={{
                              height: "170px",
                              width: "250px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                          }}
                          >
                          <CircularProgress />
                          </Box>
                      ) : is.empty(values.icon) ? (
                          <Box display="flex" flexDirection="column">
                          {/* <label htmlFor="map-marker-inuse">
                              <Typography variant="body2">
                                  Upload Tile Icon
                              </Typography>
                          </label> */}
                          <TextField
                              value={!imageUploadPending ? "" : "LOADING...."}
                              placeholder={"Product icon not selected"}
                              onChange={handleChange}
                              {...getFieldProps("icon")}
                              error={touched.icon && Boolean(errors.icon)}
                              helperText={
                              touched.icon && errors.icon
                                  ? errors.icon
                                  : `Upload an image related to query. (Max. 10MB - .JPEG, .JPG, .PNG)`
                              }
                              disabled
                          />
                          </Box>
                      ) : (
                          <div>
                          <Typography variant="body2" color="text.secondary">
                              Upload Tile Icon
                          </Typography>
                          {values?.icon && (
                              <Card
                              style={{
                                  border: "1px solid #ddd",
                                  marginBottom: 10,
                                  boxShadow: "none",
                                  width: "400px",
                              }}
                              >
                              <CardContent
                                  sx={{ display: "flex", placeContent: "center" }}
                              >
                                  <ImageList cols={1}>
                                  <ImageListItem
                                      key={`${values?.icon}-key`}
                                  >
                                      <img
                                      src={
                                          values?.icon
                                      }
                                      alt={"attachment"}
                                      style={{
                                          maxHeight: "30px",
                                          maxWidth: "30px",
                                      }}
                                      loading="eager"
                                      onClick={() => {
                                          setPhotoModal({
                                          open: true,
                                          src: values?.icon,
                                          });
                                      }}
                                      />
                                  </ImageListItem>
                                  </ImageList>
                              </CardContent>
                              </Card>
                          )}
                          </div>
                      )}
                        {!isReadOnly && (
                        <LoadingButton
                          variant="contained"
                          onClick={() => iconUploadInputRef.current.click()}
                          loading={imageUploadPending}
                          loadingIndicator="Loading…"
                          endIcon={<Icon icon="uil:image-upload"></Icon>}
                        >
                          Upload
                          <input
                          accept="image/jpeg,image/png,image/jpg"
                          type="file"
                          hidden
                          id="map-marker-inuse"
                          ref={iconUploadInputRef}
                          onChange={(e) =>
                            handleImageUpload(e, "icon")
                          }
                          />
                        </LoadingButton>
                        )}
                      </Box>
                  </Grid>
              
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      {...getFieldProps("title")}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      {...getFieldProps("description")}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="URL"
                      name="url"
                      value={values.url}
                      onChange={handleChange}
                      {...getFieldProps("url")}
                      error={touched.url && Boolean(errors.url)}
                      helperText={touched.url && errors.url}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Access Type</InputLabel>
                      <Select
                        name="access_type"
                        label="Access Type"
                        value={values.access_type}
                        onChange={handleChange}
                        {...getFieldProps("access_type")}
                        error={touched.access_type && Boolean(errors.access_type)}
                      >
                        <MenuItem value={1}>Public</MenuItem>
                        <MenuItem value={0}>Private</MenuItem>
                      </Select>
                      {touched.access_type && errors.access_type && (
                        <Typography variant="caption" color="error">
                          {errors.access_type}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        name="category"
                        label="Category"
                        value={values.category}
                        onChange={handleChange}
                        {...getFieldProps("category")}
                        error={touched.category && Boolean(errors.category)}
                      >
                          {category && category.map((option) => (
                          <MenuItem key={option.category} value={option.category}>
                              {option.category}
                          </MenuItem>
                          ))}
                      </Select>
                      {touched.category && errors.category && (
                        <Typography variant="caption" color="error">
                          {errors.category}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
                {!isReadOnly && (
                  <Stack direction="row" spacing={2} mt={3}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                )}
              </Form>
          </FormikProvider>
          {imagePreview.open && (
          <ImgPreviewModal
          imageObj={imagePreview}
          buttonsArray={imagePreviewButtons}
          />
          )}
          {photoModal && (
              <Dialog
                  onClose={() => {
                  setPhotoModal({ open: false, src: "" });
                  }}
                  aria-labelledby="customized-dialog-title"
                  open={photoModal.open}
              >
                  <IconButton
                  aria-label="close"
                  onClick={() => {
                      setPhotoModal({ open: false, src: "" });
                  }}
                  sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                  }}
                  >
                  <GridCloseIcon />
                  </IconButton>
                  <DialogContent>
                  <img src={photoModal.src} alt="Review" />
                  </DialogContent>
              </Dialog>
          )}
          </Card>
        </>
    )
}

export default ProductForm;